import type { ReactNode } from "react";
import { t } from "./kieli";

/**
 * Estelaatikko — pakollisen pätevyyden puute allokoinnissa ja sidonnassa.
 *
 * Päätös 1.10.2026 nro 3 (Toni 06:07): este oli harmaata leipätekstiä (`.huom`) napin
 * alla, eikä sitä huomannut. Sääntö ei muutu (145: sidonta sallitaan perusteella),
 * vain näkyvyys: oma laatikko värillisellä reunalla ja taustalla, varoitusmerkki,
 * otsikko lihavoituna ja isompana, puuttuva pätevyys per henkilö omalla rivillään
 * nimi korostettuna, ja perustekenttä korostettuna kunnes siinä on tekstiä.
 *
 * Sama komponentti kolmessa paikassa: ruudukon allokointilomake (Kapasiteetti),
 * tarpeen sidontalomake ja «Sido tehtävään» (Kohteet). Yksi muoto, ei kolme.
 * Väri on `--varoitus` eikä `--puna`: tila ei ole virhe vaan portti, jonka saa
 * ohittaa nimetyllä syyllä (Tila.tsx: varoitus = väri JA sana JA muoto).
 *
 * data-attribuutit annetaan nimeltä, koska kävely lukee ne (`[data-allokointi-este]`,
 * `[data-allokointi-peruste] input`, `[data-liita-peruste]`).
 */
export function Estelaatikko({ otsikko = t("muu.este.otsikko"), selite, rivit, peruste, setPeruste,
                               placeholder, esteAttr, perusteAttr, perusteNimike = t("qr.peruste"), children }: {
  otsikko?: string;
  /** Yksi lause säännöstä: mitä tapahtuu kun peruste annetaan. */
  selite?: ReactNode;
  /** Puuttuva pätevyys per henkilö — jokainen omalla rivillään. */
  rivit?: { avain: string | number; nimi: string; puuttuu: string }[];
  peruste: string;
  setPeruste: (v: string) => void;
  placeholder?: string;
  /** data-attribuutin nimi laatikolle, esim. "data-allokointi-este". */
  esteAttr?: string;
  /** data-attribuutin nimi perustekentän labelille, esim. "data-allokointi-peruste". */
  perusteAttr?: string;
  perusteNimike?: string;
  children?: ReactNode;
}) {
  const tyhja = peruste.trim() === "";
  const esteProps = esteAttr ? { [esteAttr]: "" } : {};
  const perusteProps = perusteAttr ? { [perusteAttr]: "" } : {};
  return (
    <div className="este" role="group" aria-label={otsikko} {...esteProps}>
      <p className="este__otsikko"><span className="este__merkki" aria-hidden="true">⚠</span>{otsikko}</p>
      {selite && <p className="este__selite">{selite}</p>}
      {rivit && rivit.length > 0 && (
        <ul className="este__lista">
          {rivit.map((r) => <li key={r.avain}><span className="este__nimi">{r.nimi}</span> — {r.puuttuu}</li>)}
        </ul>
      )}
      {children}
      <label className={"este__peruste" + (tyhja ? " este__peruste--tyhja" : "")} {...perusteProps}>
        {perusteNimike}{tyhja && <span className="este__vaadittu"> · {t("muu.este.pakollinen")}</span>}
        <input type="text" value={peruste} onChange={(e) => setPeruste(e.target.value)} maxLength={500}
               placeholder={placeholder} aria-required="true" />
      </label>
    </div>
  );
}
